import React, { useEffect, useRef, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { Copy, Download, Eye, Plus, Printer, Users } from "lucide-react";
import { QrPrintDialog } from "@/components/QrPrintDialog";
import { toast } from "sonner";
import { api, boothUrl, errMsg } from "@/lib/api";
import { PageHead, Panel } from "@/pages/vendor/Shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { DateField } from "@/components/DateField";
import { EventsCalendar } from "@/components/EventsCalendar";
import { formatRange } from "@/lib/dates";
import { useAuth } from "@/lib/auth";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const EMPTY = { name: "", venue: "", booth: "", start_date: "", end_date: "", quote_form_id: null, active: true };

function EventDialog({ initial, forms, types, onClose, onSaved }: any) {
  const [f, setF] = useState<any>(initial || EMPTY);
  const save = async () => {
    try {
      if (initial?.id) await api.put(`/vendor/events/${initial.id}`, f);
      else await api.post("/vendor/events", f);
      toast.success("Event saved");
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const field = (k: string, label: string, type = "text") => (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Input type={type} value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`event-${k}-input`} />
    </div>
  );
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="event-dialog">
        <DialogHeader><DialogTitle>{initial?.id ? "Edit event" : "New event / booth"}</DialogTitle></DialogHeader>
        {field("name", "Event name")}
        <div className="grid grid-cols-2 gap-3">{field("venue", "Venue")}{field("booth", "Booth #")}</div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label>Starts</Label>
            <DateField value={f.start_date} testid="event-start_date-input"
              onChange={(v) => setF({ ...f, start_date: v, end_date: f.end_date && v && f.end_date < v ? "" : f.end_date })} />
          </div>
          <div className="space-y-1.5">
            <Label>Ends</Label>
            <DateField value={f.end_date} minDate={f.start_date} testid="event-end_date-input" onChange={(v) => setF({ ...f, end_date: v })} />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label>Event type {!initial?.id && <span className="text-rose-600">*</span>}</Label>
          <Select value={f.event_type_id || ""} onValueChange={(v) => setF({ ...f, event_type_id: v })}>
            <SelectTrigger data-testid="event-type-select"><SelectValue placeholder="Choose an event type" /></SelectTrigger>
            <SelectContent>{types.map((t: any) => <SelectItem key={t.id} value={t.id}>{t.name}{t.typical ? " ★" : ""}</SelectItem>)}</SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">Its standard questions are asked at this booth automatically. Attendees who already answered them share instantly.</p>
        </div>
        <div className="space-y-1.5">
          <Label>Your extra questions (optional)</Label>
          <Select value={f.quote_form_id || "default"} onValueChange={(v) => setF({ ...f, quote_form_id: v === "default" ? null : v })}>
            <SelectTrigger data-testid="event-form-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="default">Default (first form)</SelectItem>
              {forms.map((fm: any) => <SelectItem key={fm.id} value={fm.id}>{fm.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button onClick={save} className="bg-primary text-white hover:bg-primary/90" data-testid="event-save-button">Save event</Button>
      </DialogContent>
    </Dialog>
  );
}

function EventCard({ e, onEdit, onToggle, onChanged }: any) {
  const ref = useRef<HTMLDivElement>(null);
  const [printing, setPrinting] = useState(false);
  const qr = boothUrl(e.slug);
  const nfc = boothUrl(e.slug, "nfc");
  const prereg = `${window.location.origin}/r/${e.slug}`;
  const copy = (url: string, msg: string) => { navigator.clipboard.writeText(url); toast.success(msg); };
  const download = () => {
    const canvas = ref.current?.querySelector("canvas");
    if (!canvas) return;
    const a = document.createElement("a");
    a.href = canvas.toDataURL("image/png");
    a.download = `${e.name}-qr.png`;
    a.click();
  };
  return (
    <Panel className={`rise flex min-w-0 flex-col gap-4 sm:flex-row ${e.active ? "" : "opacity-60"}`} data-testid={`event-card-${e.id}`}>
      <div ref={ref} className="shrink-0 self-start rounded-sm border bg-white p-2"><QRCodeCanvas value={qr} size={140} fgColor="#1C1917" /></div>
      <div className="min-w-0 flex-1 space-y-2">
        <div className="flex items-start justify-between gap-2">
          <div>
            <h3 className="text-lg font-semibold">{e.name}</h3>
            <span className={`mt-0.5 inline-block rounded-sm px-1.5 py-0.5 text-[10px] font-semibold ${e.event_type_name ? "bg-primary/10 text-primary" : "bg-amber-50 text-amber-700"}`} data-testid={`event-type-badge-${e.id}`}>{e.event_type_name || "No event type — edit to choose one"}</span>
            <p className="text-xs text-muted-foreground">{[e.venue, e.booth && `Booth ${e.booth}`].filter(Boolean).join(" · ")}</p>
            {e.start_date && <p className="mt-0.5 text-xs font-semibold text-primary" data-testid={`event-dates-${e.id}`}>{formatRange(e.start_date, e.end_date)}</p>}
          </div>
          <Switch checked={e.active} onCheckedChange={(v) => onToggle(e, v)} data-testid={`event-active-switch-${e.id}`} />
        </div>
        <p className="truncate rounded-sm bg-muted px-2 py-1.5 font-mono text-xs text-emerald-700" data-testid={`event-booth-url-${e.id}`}>{qr}</p>
        <div className="flex flex-wrap gap-x-4 gap-y-1 font-mono text-xs text-muted-foreground">
          <span className="flex items-center gap-1"><Eye className="h-3.5 w-3.5" /> {e.views} views</span>
          <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" /> {e.leads} leads</span>
          <span data-testid={`event-prereg-count-${e.id}`}>{e.preregistered} pre-registered · {e.checked_in} checked in</span>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" onClick={download} className="bg-primary text-white hover:bg-primary/90" data-testid={`event-download-qr-${e.id}`}><Download className="mr-1 h-3.5 w-3.5" /> Download QR</Button>
          <Button size="sm" variant="secondary" onClick={() => setPrinting(true)} data-testid={`event-print-qr-${e.id}`}><Printer className="mr-1 h-3.5 w-3.5" /> Print</Button>
          {printing && <QrPrintDialog event={e} url={qr} onClose={() => setPrinting(false)} onChanged={onChanged} />}
          <Button size="sm" variant="secondary" onClick={() => copy(qr, "Booth link copied")} data-testid={`event-copy-link-${e.id}`}><Copy className="mr-1 h-3.5 w-3.5" /> Copy link</Button>
          <Button size="sm" variant="ghost" onClick={() => window.open(qr, "_blank")} data-testid={`event-preview-${e.id}`}>Preview</Button>
          <Button size="sm" variant="ghost" onClick={onEdit} data-testid={`event-edit-${e.id}`}>Edit</Button>
        </div>
        <div className="flex items-center gap-2 rounded-sm border p-2 text-xs" data-testid={`event-prereg-row-${e.id}`}>
          <span className="shrink-0 font-semibold">Pre-registration link</span>
          <code className="min-w-0 flex-1 truncate rounded-sm bg-muted px-2 py-1" data-testid={`event-prereg-url-${e.id}`}>{prereg}</code>
          <Button size="sm" variant="secondary" onClick={() => copy(prereg, "Pre-registration link copied — share it before the event")} data-testid={`event-copy-prereg-${e.id}`}>Copy</Button>
        </div>
        <details className="rounded-sm border border-dashed p-2 text-xs" data-testid={`event-nfc-advanced-${e.id}`}>
          <summary className="cursor-pointer font-semibold text-muted-foreground">Advanced: NFC tag (optional)</summary>
          <p className="mt-2 text-muted-foreground">Write this link to an NFC sticker with any NFC writer app. Scans are tracked separately from QR.</p>
          <div className="mt-2 flex items-center gap-2">
            <code className="min-w-0 flex-1 truncate rounded-sm bg-muted px-2 py-1" data-testid={`event-nfc-url-${e.id}`}>{nfc}</code>
            <Button size="sm" variant="secondary" onClick={() => copy(nfc, "NFC link copied")} data-testid={`event-copy-nfc-${e.id}`}>Copy</Button>
          </div>
        </details>
      </div>
    </Panel>
  );
}

export default function Events() {
  const [events, setEvents] = useState<any[] | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [forms, setForms] = useState<any[]>([]);
  const [editing, setEditing] = useState<any>(undefined);
  const [types, setTypes] = useState<any[]>([]);
  const { vendor } = useAuth();
  const load = () => api.get("/vendor/events").then((r) => { setEvents(r.data); setLoadError(false); })
    .catch((e) => { setLoadError(true); toast.error(errMsg(e)); });
  useEffect(() => {
    load();
    api.get("/vendor/forms").then((r) => setForms(r.data));
    const typical: string[] = vendor?.vendor?.typical_event_types || [];
    api.get("/event-types").then((r) => setTypes(r.data.map((t: any) => ({ ...t, typical: typical.includes(t.id) }))
      .sort((a: any, b: any) => Number(b.typical) - Number(a.typical))));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const toggle = async (e: any, active: boolean) => {
    try {
      const { views, leads, preregistered, checked_in, ...rest } = e;
      await api.put(`/vendor/events/${e.id}`, { ...rest, active });
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <div>
      <PageHead eyebrow="Booth QR codes" title="Events & booths">
        <Button onClick={() => setEditing(null)} className="bg-primary text-white hover:bg-primary/90" data-testid="events-new-button"><Plus className="mr-1 h-4 w-4" /> New event</Button>
      </PageHead>
      <p className="mb-5 max-w-2xl text-sm text-muted-foreground">Print each booth's QR code on your signage, table cards or badges. Every scan opens your booth page. NFC tags are an optional add-on under each event.</p>
      {loadError && !events && (
        <Panel className="text-center text-sm" data-testid="events-load-error">
          We couldn't load your events. <Button size="sm" variant="secondary" className="ml-2" onClick={load} data-testid="events-retry-button">Try again</Button>
        </Panel>
      )}
      {!events && !loadError && <p className="text-sm text-muted-foreground" data-testid="events-loading">Loading your events…</p>}
      {events && events.length > 0 && <EventsCalendar events={events} onOpen={(e) => document.querySelector(`[data-testid="event-card-${e.id}"]`)?.scrollIntoView({ behavior: "smooth", block: "start" })} />}
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        {(events || []).map((e) => <EventCard key={e.id} e={e} onEdit={() => setEditing(e)} onToggle={toggle} onChanged={load} />)}
      </div>
      {events?.length === 0 && <Panel data-testid="events-empty" className="text-center text-sm text-muted-foreground">Create your first event to get a booth QR code.</Panel>}
      {editing !== undefined && <EventDialog initial={editing} forms={forms} types={types} onClose={() => setEditing(undefined)} onSaved={() => { setEditing(undefined); load(); }} />}
    </div>
  );
}
